import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  RocketIcon, PlayIcon, StopCircleIcon, FileTextIcon, BriefcaseIcon,
  CheckCircleIcon, AlertTriangleIcon, UserIcon
} from '../icons';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';

/**
 * AutoJobApplyPane — Electron-native Quick Apply.
 *
 * The web build relies on the Terview Chrome extension to drive job
 * platforms. Desktop users get an equivalent flow without installing
 * anything: the main process opens a dedicated, persistent-session
 * BrowserWindow on LinkedIn/Indeed/etc., injects the same content
 * scripts the extension uses (see chrome-extension/content/*), and a
 * preload polyfill emulates chrome.runtime + chrome.storage.local so
 * those scripts run unmodified.
 *
 * This component:
 *   • collects preferences + resume choice locally,
 *   • fires `terview:autoapply-open` to spin up the controller,
 *   • subscribes to `terview:autoapply-status` for live progress, and
 *   • exposes a Stop button that fires `terview:autoapply-close`.
 */

const PLATFORMS = [
  { key: 'linkedin',     label: 'LinkedIn',     blurb: 'Easy Apply jobs',          accent: '#0a66c2' },
  { key: 'indeed',       label: 'Indeed',       blurb: 'Apply with Indeed Resume', accent: '#2557a7' },
  { key: 'dice',         label: 'Dice',         blurb: 'Easy Apply tech roles',    accent: '#ff5722' },
  { key: 'monster',      label: 'Monster',      blurb: '1-click Monster apply',    accent: '#6e46ae' },
  { key: 'ziprecruiter', label: 'ZipRecruiter', blurb: 'Quick Apply listings',     accent: '#1a89e0' },
];

function getApiBase() {
  const env = process.env.REACT_APP_API_BASE_URL;
  if (env) return env;
  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  if (origin.startsWith('http://') || origin.startsWith('https://')) return origin;
  return 'http://localhost:5000';
}

export default function AutoJobApplyPane() {
  const API_BASE_URL = getApiBase();

  // ── Form state ────────────────────────────────────────────────────────────
  const [jobTitles, setJobTitles] = useState('Software Engineer');
  const [locations, setLocations] = useState('United States');
  const [jobType, setJobType] = useState('Full-time');
  const [remote, setRemote] = useState('Any');
  const [maxApplications, setMaxApplications] = useState(20);
  const [platforms, setPlatforms] = useState({
    linkedin: true, indeed: false, dice: false, monster: false, ziprecruiter: false,
  });

  // ── Profile + resume ──────────────────────────────────────────────────────
  const [profile, setProfile] = useState(null);
  const [profileResume, setProfileResume] = useState(null);
  const [resumeChoice, setResumeChoice] = useState('profile'); // 'profile' | 'upload'
  const [uploadedResumeName, setUploadedResumeName] = useState(null);
  const fileInputRef = useRef(null);

  // ── Live state from main process ──────────────────────────────────────────
  const [isRunning, setIsRunning] = useState(false);
  const [stats, setStats] = useState({ applied: 0, skipped: 0, failed: 0, byPlatform: {} });
  const [logs, setLogs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [currentPlatform, setCurrentPlatform] = useState(null);
  const [error, setError] = useState(null);

  // ── Hydrate profile + saved resume on mount ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await res.json();
        if (cancelled || !data?.profile) return;
        setProfile(data.profile);
        if (data.profile.resumeFilename) {
          setProfileResume(data.profile.resumeFilename);
          setResumeChoice('profile');
        }
        // Pre-fill location from profile if user hasn't typed yet.
        if (data.profile.location && locations === 'United States') {
          setLocations(data.profile.location);
        }
      } catch (_) { /* offline / no profile yet — leave defaults */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  // ── Subscribe to main-process status broadcasts ──────────────────────────
  useEffect(() => {
    const ipc = window.electron?.ipcRenderer;
    if (!ipc) return;
    const onStatus = (_evt, payload) => {
      setIsRunning(!!payload.running);
      if (payload.stats) setStats(payload.stats);
      if (payload.logs) setLogs(payload.logs);
      if (payload.appliedJobs) setAppliedJobs(payload.appliedJobs);
      if (payload.currentPlatform) setCurrentPlatform(payload.currentPlatform);
    };
    const onClosed = () => {
      setIsRunning(false);
      setCurrentPlatform(null);
    };
    const onError = (_evt, payload) => {
      setError(payload?.error || 'Unknown auto-apply error');
      setIsRunning(false);
    };
    ipc.on('terview:autoapply-status', onStatus);
    ipc.on('terview:autoapply-closed', onClosed);
    ipc.on('terview:autoapply-error', onError);
    return () => {
      ipc.removeAllListeners('terview:autoapply-status');
      ipc.removeAllListeners('terview:autoapply-closed');
      ipc.removeAllListeners('terview:autoapply-error');
    };
  }, []);

  // ── Resume upload — POST to /api/upload-resume, then use the returned
  //    filename as if it were a profile resume. Same endpoint the web
  //    flow uses, so a single source of truth on the server.
  const onUploadFile = useCallback(async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('resume', file);
      const res = await fetch(`${API_BASE_URL}/api/upload-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: fd,
      });
      const data = await res.json();
      if (data?.filename) {
        setUploadedResumeName(data.filename);
        setResumeChoice('upload');
      } else {
        setError(data?.message || 'Resume upload failed');
      }
    } catch (e) {
      setError(`Upload failed: ${e.message}`);
    }
  }, [API_BASE_URL]);

  const togglePlatform = (key) => {
    setPlatforms(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedPlatforms = Object.entries(platforms).filter(([, v]) => v).map(([k]) => k);
  const resumeFilename = resumeChoice === 'upload' ? uploadedResumeName : profileResume;
  const canLaunch = (
    selectedPlatforms.length > 0 &&
    jobTitles.trim().length > 0 &&
    locations.trim().length > 0 &&
    !!resumeFilename
  );

  const gate = useFeatureGate('autoApply');

  const handleLaunch = async () => {
    setError(null);
    const ipc = window.electron?.ipcRenderer;
    if (!ipc) {
      setError('Desktop bridge not available. Restart Terview.');
      return;
    }
    if (gate.locked) { gate.openPaywall(); return; }
    // Per-feature credit gate via the shared hook — refreshes the
    // counter chip and opens the paywall automatically on 403.
    const { ok, reason } = await gate.deduct();
    if (!ok) {
      if (reason !== 'no-credits') setError(reason || 'Could not start Auto Apply');
      return;
    }
    const preferences = {
      jobTitles,
      locations,
      jobType,
      remote,
      maxApplications: String(maxApplications),
      platforms,
      resumeFilename,
      apiBase: API_BASE_URL,
    };
    ipc.send('terview:autoapply-open', {
      preferences,
      token: localStorage.getItem('token') || '',
      profile: profile || {},
    });
    setIsRunning(true);
  };

  const handleStop = () => {
    const ipc = window.electron?.ipcRenderer;
    if (!ipc) return;
    ipc.send('terview:autoapply-close');
  };

  const total = stats.applied + stats.skipped + stats.failed;
  const progressPct = isRunning && maxApplications > 0
    ? Math.min(100, Math.round((stats.applied / Number(maxApplications)) * 100))
    : 0;

  return (
    <div className="ajp-electron-pane">
      <div className="pane" style={{ maxWidth: 'none', marginBottom: 14 }}>
        <div className="pane-eyebrow">
          <RocketIcon size={11} /> Auto Apply
        </div>
        <h1 className="pane-title">Auto Job Apply</h1>
        <p className="pane-subtitle">
          One-click apply on LinkedIn, Indeed, Dice, Monster &amp; ZipRecruiter — no extension required.
          The desktop app opens each platform in its own window and runs the apply flow for you.
        </p>
      </div>

      {/* ── Launcher row ─────────────────────────────────────────────────── */}
      <div className="aap-launcher">
        {!isRunning ? (
          <button
            className="aap-launch-btn"
            onClick={handleLaunch}
            disabled={!canLaunch || gate.locked}
            title={gate.locked
              ? 'Out of free credits — upgrade to keep using'
              : !canLaunch ? 'Select at least one platform and a resume' : 'Start Auto Apply'}
          >
            <PlayIcon size={14} /> Launch Quick Apply (Desktop) <FreeCreditsLabel feature="autoApply" />
          </button>
        ) : (
          <button className="aap-stop-btn" onClick={handleStop}>
            <StopCircleIcon size={14} /> Stop Auto Apply
          </button>
        )}
        <div className={`aap-status-pill ${isRunning ? 'live' : ''}`}>
          <span className="aap-status-dot" />
          {isRunning
            ? (currentPlatform ? `Applying on ${currentPlatform}…` : 'Launching…')
            : 'Ready'}
        </div>
        {error && (
          <div className="aap-error-pill" onClick={() => setError(null)} title="Click to dismiss">
            <AlertTriangleIcon size={12} /> {error}
          </div>
        )}
      </div>

      {/* ── Progress (only while running or after a run) ─────────────── */}
      {(isRunning || total > 0) && (
        <div className="aap-progress">
          <div className="aap-progress-stats">
            <div className="aap-stat applied">
              <div className="aap-stat-val">{stats.applied}</div>
              <div className="aap-stat-lbl">Applied</div>
            </div>
            <div className="aap-stat skipped">
              <div className="aap-stat-val">{stats.skipped}</div>
              <div className="aap-stat-lbl">Skipped</div>
            </div>
            <div className="aap-stat failed">
              <div className="aap-stat-val">{stats.failed}</div>
              <div className="aap-stat-lbl">Failed</div>
            </div>
            <div className="aap-stat target">
              <div className="aap-stat-val">{maxApplications}</div>
              <div className="aap-stat-lbl">Target</div>
            </div>
          </div>
          <div className="aap-progress-bar">
            <div className="aap-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          <div className="aap-progress-grid">
            <div className="aap-applied-list">
              <div className="aap-section-title">Recent applications</div>
              {appliedJobs.length === 0 && <div className="aap-empty">No jobs yet — sit tight.</div>}
              {appliedJobs.slice(-10).reverse().map((j, i) => (
                <div key={i} className={`aap-applied-row status-${j.status || 'applying'}`}>
                  <div className="aap-applied-title">{j.title || 'Untitled role'}</div>
                  <div className="aap-applied-meta">
                    {j.company || 'Unknown company'} · {j.platform} · {j.status || 'applying'}
                  </div>
                </div>
              ))}
            </div>
            <div className="aap-log-list">
              <div className="aap-section-title">Live log</div>
              {logs.length === 0 && <div className="aap-empty">No log lines yet.</div>}
              {logs.slice(-20).reverse().map((l, i) => (
                <div key={i} className={`aap-log-row type-${l.type || 'info'}`}>
                  <span className="aap-log-time">{l.time}</span>
                  <span className="aap-log-msg">{l.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Setup form ──────────────────────────────────────────────── */}
      {!isRunning && (
        <div className="pane" style={{ maxWidth: 'none', marginTop: 14 }}>
          <div className="ajp-form-grid">
            {/* LEFT: Job preferences */}
            <section className="ajp-prefs-section">
              <h3 className="aap-h3"><BriefcaseIcon size={12} /> Job preferences</h3>
              <label className="aap-field">
                <span>Job titles</span>
                <input
                  type="text" value={jobTitles}
                  onChange={(e) => setJobTitles(e.target.value)}
                  placeholder="Software Engineer, Backend Developer"
                />
              </label>
              <label className="aap-field">
                <span>Locations</span>
                <input
                  type="text" value={locations}
                  onChange={(e) => setLocations(e.target.value)}
                  placeholder="United States, Remote"
                />
              </label>
              <div className="aap-row-2">
                <label className="aap-field">
                  <span>Job type</span>
                  <select value={jobType} onChange={(e) => setJobType(e.target.value)}>
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Internship</option>
                  </select>
                </label>
                <label className="aap-field">
                  <span>Remote</span>
                  <select value={remote} onChange={(e) => setRemote(e.target.value)}>
                    <option>Any</option>
                    <option>Remote</option>
                    <option>Hybrid</option>
                    <option>On-site</option>
                  </select>
                </label>
              </div>
              <label className="aap-field">
                <span>Max applications this session</span>
                <input
                  type="number" min={1} max={100}
                  value={maxApplications}
                  onChange={(e) => setMaxApplications(parseInt(e.target.value || '20', 10))}
                />
              </label>
            </section>

            {/* RIGHT: Platforms */}
            <section className="ajp-platforms-section">
              <h3 className="aap-h3"><RocketIcon size={12} /> Job platforms</h3>
              <div className="ajp-platforms">
                {PLATFORMS.map(p => (
                  <button
                    key={p.key}
                    type="button"
                    className={`aap-platform-card ${platforms[p.key] ? 'on' : ''}`}
                    onClick={() => togglePlatform(p.key)}
                    style={{ '--accent': p.accent }}
                  >
                    <div className="aap-platform-head">
                      <div className="aap-platform-name">{p.label}</div>
                      <div className={`aap-platform-toggle ${platforms[p.key] ? 'on' : ''}`}>
                        {platforms[p.key] && <CheckCircleIcon size={12} />}
                      </div>
                    </div>
                    <div className="aap-platform-blurb">{p.blurb}</div>
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* Resume picker */}
          <div style={{ marginTop: 14 }}>
            <h3 className="aap-h3"><FileTextIcon size={12} /> Resume</h3>
            <div className="es-resume-choices">
              <button
                type="button"
                className={`es-choice ${resumeChoice === 'profile' ? 'selected' : ''} ${!profileResume ? 'disabled' : ''}`}
                onClick={() => profileResume && setResumeChoice('profile')}
                disabled={!profileResume}
              >
                <div className="es-choice-radio">
                  {resumeChoice === 'profile' && <div className="es-choice-radio-dot" />}
                </div>
                <div className="es-choice-body">
                  <div className="es-choice-title"><UserIcon size={11} /> Use profile resume</div>
                  <div className="es-choice-sub">
                    {profileResume ? profileResume : 'No profile resume on file'}
                  </div>
                </div>
              </button>
              <button
                type="button"
                className={`es-choice ${resumeChoice === 'upload' ? 'selected' : ''}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="es-choice-radio">
                  {resumeChoice === 'upload' && <div className="es-choice-radio-dot" />}
                </div>
                <div className="es-choice-body">
                  <div className="es-choice-title"><FileTextIcon size={11} /> Upload new resume</div>
                  <div className="es-choice-sub">
                    {uploadedResumeName || 'Click to pick a PDF or DOCX'}
                  </div>
                </div>
                <input
                  ref={fileInputRef} type="file" accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={(e) => onUploadFile(e.target.files?.[0])}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
